// A button is labelable too, but a field holding an input and a "Show"
// button is still about the input. A switch is the one button that is the
// field's control.
const SWITCH = 'button[role="switch"]';
const LABELABLE = `input:not([type="hidden"]), select, textarea, ${SWITCH}`;
const ERRORS = '[data-form-error]';
const HELP = '.form-group__help';
const LABEL = '[data-field-label]';
const LABEL_TEXT = '[data-field-label-text]';
const REQUIRED = 'data-field-required';
const REQUIRED_MARKER = '[data-field-required-marker]';
// A dropdown's filter box lives inside its menu. It is a control, but not the
// one this field is about — the trigger beside it is.
const INSIDE_A_MENU = '[role="listbox"], [role="menu"]';

let fieldIds = 0;

const MANAGED_DESCRIPTION = /^elm-field-\d+-description-\d+$/;
const MANAGED_LABEL = /^elm-field-\d+-label$/;

function describe(control, descriptionIds) {
  // Ids we did not generate are the caller's and survive; the Set keeps a
  // preserved one from being appended again on every rewire.
  const ids = [
    ...new Set(
      (control.getAttribute('aria-describedby') ?? '')
        .split(' ')
        .filter((id) => id && !MANAGED_DESCRIPTION.test(id))
        .concat(descriptionIds),
    ),
  ];

  if (ids.length === 0) control.removeAttribute('aria-describedby');
  else control.setAttribute('aria-describedby', ids.join(' '));
}

// Only the name this component gave. A control named by anyone else keeps it.
function unname(control) {
  if (MANAGED_LABEL.test(control.getAttribute('aria-labelledby') ?? '')) {
    control.removeAttribute('aria-labelledby');
  }
}

class ElmField extends HTMLElement {
  connectedCallback() {
    this.fieldId ??= `elm-field-${++fieldIds}`;
    this.wire();
    this.observer = new MutationObserver(() => this.wire());
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  own(selector) {
    return Array.from(this.querySelectorAll(selector)).filter((el) => el.closest('elm-field') === this);
  }

  describedBy(node, controls) {
    for (let el = node.parentElement; el; el = el.parentElement) {
      const within = controls.filter((control) => el.contains(control));
      if (within.length === 1) return within;
      if (within.length > 1) return controls;
      if (el === this) break;
    }
    return controls;
  }

  wire() {
    const controls = this.own(LABELABLE).filter((el) => !el.closest(INSIDE_A_MENU));
    const described = new Map(controls.map((control) => [control, []]));

    this.own(`${ERRORS}, ${HELP}`).forEach((node, index) => {
      // Ours are positional and get reassigned every wire, so a block
      // appearing earlier cannot take an id another block is still using.
      // An id we did not mint is the caller's and is left alone.
      if (!node.id || MANAGED_DESCRIPTION.test(node.id)) {
        node.id = `${this.fieldId}-description-${index}`;
      }
      this.describedBy(node, controls).forEach((control) => described.get(control).push(node.id));
    });

    controls.forEach((control) => describe(control, described.get(control)));

    // FormGroup.required is a flag on the whole field, so it can only be spent
    // on a control when there is exactly one — on a group it would call every
    // part required, including the optional ones. When it cannot be spent, the
    // asterisk goes back to being read, since it is then the only signal left.
    const marker = this.own(REQUIRED_MARKER)[0];
    const onControl = this.hasAttribute(REQUIRED) && controls.length === 1;

    controls.forEach((control) => control.removeAttribute('aria-required'));
    if (onControl) {
      controls[0].setAttribute('aria-required', 'true');
      marker?.setAttribute('aria-hidden', 'true');
    } else {
      marker?.removeAttribute('aria-hidden');
    }

    const [label] = this.own(LABEL);
    if (!label) {
      this.removeAttribute('role');
      this.removeAttribute('aria-labelledby');
      controls.forEach(unname);
      return;
    }

    const [labelText] = this.own(LABEL_TEXT);
    const [control] = controls;
    if (controls.length === 1 && !control.closest('label')) {
      control.id ||= `${this.fieldId}-control`;
      label.htmlFor = control.id;
      this.removeAttribute('role');
      this.removeAttribute('aria-labelledby');
      // A switch names itself, and its name carries the reason it is
      // disabled; aria-labelledby would win over it and drop the reason.
      if (labelText && !control.matches(SWITCH)) {
        labelText.id ||= `${this.fieldId}-label`;
        control.setAttribute('aria-labelledby', labelText.id);
      } else {
        unname(control);
      }
    } else {
      const nameSource = labelText ?? label;
      nameSource.id ||= `${this.fieldId}-label`;
      label.removeAttribute('for');
      this.setAttribute('role', 'group');

      // The marker names nothing on its own, so when no control could take
      // aria-required the group's name has to carry it.
      const names = [nameSource];
      if (marker && !onControl) {
        marker.id ||= `${this.fieldId}-required`;
        names.push(marker);
      }
      this.setAttribute('aria-labelledby', names.map((node) => node.id).join(' '));
      // The field names the group now; a control still carrying our name from
      // when it was the only one would be announced twice.
      controls.forEach(unname);
    }
  }
}

if (!customElements.get('elm-field')) {
  customElements.define('elm-field', ElmField);
}
